import {
  FingeringProcessResponse,
  FingeringResult,
  FingeringPart,
  FingeringMode,
  PdfColorMode,
} from '../types/fingering';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * issue #96: backend (Azure Functions proxy) 廃止に伴い、 submit/status/result すべて
 * REACT_APP_API_URL (= score-engine の直 URL) を基点に score-engine を直接叩く。
 * 旧 issue #80 案 B の REACT_APP_SCORE_ENGINE_DIRECT_URL による「POST だけ直送・
 * GET は SWA 経由」の二重構造は撤廃した (env 自体が直 URL になり分岐が不要になったため)。
 */
function getSubmitUrl(): string {
  return `${API_BASE_URL}/fingering/jobs`;
}

/** polling 間隔 (ミリ秒)。短時間処理でも体感を保てるよう短めに設定 (設計 §7.1)。 */
const POLL_INTERVAL_MS = 1500;

/**
 * 404→expired を即 throw にしない猶予回数 (issue #36 F-2)。
 * server cold start / reload 直後の transient 404 で処理中ジョブを取りこぼさないため、
 * expired を連続 N 回見て初めて throw する。 processing 等を見たらカウンタをリセットする。
 * useJobStatusPolling.MAX_EXPIRED_RETRY_COUNT と同義 (こちらは hook 非依存の polling ループ用)。
 */
const EXPIRED_RETRY_BUDGET = 5;

function base64ToBlob(b64: string, type: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type });
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export interface FingeringProcessFromPdfRequest {
  pdfBlob: Blob;
  /**
   * issue #131: multipart の filename に使う元ファイル名。 指定時は
   * form.append('file', blob, fileName) で送り、 name 無し Blob (部分ページ抽出 Blob 等) でも
   * filename が "blob" に化けないようにする (backend 自動保存の blob.omfz / blob.pdf 化を防ぐ)。
   */
  fileName?: string;
  /**
   * issue #152: アップロード時の元タイトル (拡張子なし)。 backend は original_title を受けて
   * PDF / MP3 / omfz 名を (元タイトル, part, mode) の純関数で組む。 未指定時 backend は
   * multipart filename から legacy derive する (後方互換)。
   */
  originalTitle?: string;
  part: FingeringPart;
  mode: FingeringMode;
  scordatura?: string;
  bpm?: number;
  output?: string;
  /** PDF 出力の運指バッジ色モード (issue #32 / #39 でリネーム)。 省略時は score-engine 側 default ('color')。 */
  colorMode?: PdfColorMode;
  /** issue #67: backend 自動保存用 Drive access token。 未指定なら auto-save スキップ。 */
  driveAccessToken?: string;
  /** issue #67: backend 自動保存先 Drive folder ID。 未指定なら appDataFolder default (backend 側で決定)。 */
  driveParentFolderId?: string;
  /** issue #98: ジョブ完了通知メールの送信先。 未指定なら通知しない (backend は notify_email キーで受ける)。 */
  notifyEmail?: string;
  /** issue #111: 自動保存フラグ。 backend は auto_save キー (== 'true') で on_done の Drive 保存/通知をガードする。 */
  autoSave?: boolean;
}

/**
 * issue #148: エディタ再生成 (from_json) の完了時通知 / 自動保存に関わる追加フィールド。
 * from_pdf 投入 (FingeringProcessFromPdfRequest) の notify_email / auto_save と対称化し、
 * regenerateBoth / regeneratePdf / regenerateAudio の末尾 opts で受け取る。
 * 未指定フィールドは FormData に積まない (backend は欠落を通知なし / 既定扱い)。
 */
export interface RegenerateNotifyOptions {
  /** ジョブ完了通知メールの送信先 (backend は notify_email キーで受ける)。 未指定なら通知しない。 */
  notifyEmail?: string;
  /** 自動保存フラグ (backend は auto_save == 'true' で on_done の通知をガード)。 */
  autoSave?: boolean;
  /** 通知メール等に載せる結果ファイル名 (backend は file_name キーで受ける)。 */
  fileName?: string;
  /**
   * issue #152: 命名ソースとなる元タイトル (backend は original_title キーで受ける)。
   * 再生成でも MP3 / omfz 名を (元タイトル, part, mode) の純関数で組ませ、 サフィックス重複を防ぐ。
   */
  originalTitle?: string;
}

/**
 * issue #148: RegenerateNotifyOptions を from_json 系 FormData に積む共通ヘルパ。
 * notify_email / auto_save(String(bool)) / file_name を append する (未指定は積まない)。
 * issue #152: original_title も同経路で積む (命名ソースの一本化)。
 */
function appendNotifyFields(form: FormData, opts?: RegenerateNotifyOptions): void {
  if (!opts) return;
  if (opts.notifyEmail) form.append('notify_email', opts.notifyEmail);
  if (opts.autoSave !== undefined) form.append('auto_save', String(opts.autoSave));
  if (opts.fileName) form.append('file_name', opts.fileName);
  if (opts.originalTitle) form.append('original_title', opts.originalTitle);
}

/** ジョブ種別。score-engine の job_type と一致する (設計 §1.4 / §3.3)。 */
export type JobType = 'from_pdf' | 'from_json_both' | 'from_json_pdf' | 'from_json_audio';

/** ジョブ進行状態。 'expired' は 404 (TTL 切れ / 不存在) を表すクライアント側ステータス。
 *  'error_processing' は score-engine が失敗時に返す status (issue #41)。 'failed' と同等にマップする。 */
export type JobStatusValue = 'queued' | 'processing' | 'done' | 'failed' | 'expired' | 'error_processing';

/**
 * ジョブ失敗時の error_kind (issue #54)。 score-engine が構造化して返す失敗種別。
 * 既知 literal を列挙しつつ、 将来追加 (audio_timeout 等) は string で受け入れる。
 */
export type JobErrorKind = 'omr_timeout' | string;

/**
 * 処理失敗を表す Error 拡張 (issue #54)。 error_kind を持ち回って UI 側で文言分岐に使う。
 * 旧 throw new Error は本 class に置換 (errorKind 省略可、 後方互換)。
 * issue #92: notFound フラグを追加。 404 (job/result 不存在) を呼び出し側が transient 失敗と
 * 区別するために使う (restoreJobResultCore が 404 を即 'gone' 判定するため)。
 */
export class ProcessingError extends Error {
  errorKind?: JobErrorKind;
  notFound?: boolean;
  constructor(message: string, errorKind?: JobErrorKind, notFound?: boolean) {
    super(message);
    this.name = 'ProcessingError';
    this.errorKind = errorKind;
    this.notFound = notFound;
  }
}

/**
 * issue #115 T3: ジョブ投入 (POST /fingering/jobs) の timeout (ms)。
 * score-engine は F1 Free のアイドルアンロードで cold start が 30 秒超になり得るため、
 * 「待てば通る」を成立させるべく長め (90 秒) に設定する。 到達時は投入を諦め、
 * {@link SERVER_STARTING_ERROR_KIND} の ProcessingError で reject する。
 */
export const SUBMIT_TIMEOUT_MS = 90_000;

/** issue #115 T3: submit timeout (cold start 中の可能性) を表す errorKind。 UI で文言分岐に使う。 */
export const SERVER_STARTING_ERROR_KIND = 'server_starting';

/**
 * issue #115 T3: AbortController + 延長 timeout 付きの POST。
 * timeout 到達 (AbortError) は「サーバ起動中の可能性」の ProcessingError にマップする。
 */
async function postFormWithTimeout(url: string, body: FormData): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);
  try {
    return await fetch(url, { method: 'POST', body, signal: controller.signal });
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new ProcessingError(
        'サーバ起動中の可能性があります。初回は最大60秒ほどかかります。少し待ってから再度お試しください。',
        SERVER_STARTING_ERROR_KIND,
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/** getJobStatus の戻り値 (GET /fingering/jobs/<id>)。 */
export interface JobStatusResponse {
  status: JobStatusValue;
  error?: string;
  /** score-engine が返す失敗種別 (issue #54)。 backend proxy は透過。 未指定時は undefined。 */
  errorKind?: JobErrorKind;
  queuePosition?: number;
}

/** processFingeringAsync の進捗通知ペイロード。 */
export interface JobProgress {
  /** ジョブ ID。投入直後の queued 通知から付与され、リロード復元の保存に使う。 */
  jobId: string;
  status: JobStatusValue;
  queuePosition?: number;
}

/** regenerateBoth の結果。 */
export interface RegenerateBothResult {
  pdfBlob: Blob;
  audioBlob: Blob;
  audioFileName: string;
}

/** regenerateAudio の結果。 */
export interface RegenerateAudioResult {
  audioBlob: Blob;
  audioFileName: string;
  detectedBpm?: number;
}

/** done で停止扱いとなる状態。 */
const SUCCESS_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>(['done']);
/** error 扱いで停止する状態。
 *  'error_processing' は score-engine が失敗時に返す status (issue #41)。 'failed' と同等にマップ。 */
const FAILURE_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>([
  'failed',
  'expired',
  'error_processing',
]);

/** from_pdf / from_json_both / from_json_pdf の result を FingeringResult に変換する。 */
function parsePdfResult(body: FingeringProcessResponse): FingeringResult {
  const result: FingeringResult = {
    pdfBlob: base64ToBlob(body.pdf, 'application/pdf'),
  };
  if (body.fingering) {
    result.fingeringJson = JSON.stringify(body.fingering);
  }
  if (body.audio) {
    result.audioBlob = base64ToBlob(body.audio, 'audio/mpeg');
    result.audioFileName = body.audio_filename;
  }
  result.detectedBpm = body.detected_bpm;
  if (body.mxl) {
    result.mxlBlob = base64ToBlob(body.mxl, 'application/vnd.recordare.musicxml');
  }
  if (body.background_pdf) {
    result.backgroundPdfBlob = base64ToBlob(body.background_pdf, 'application/pdf');
  }
  if (body.auto_save_status) {
    // issue #67: backend 自動保存 (drive_uploader) のステータスを result に乗せる
    result.autoSaveStatus = body.auto_save_status;
  }
  if (body.skipped_pages && body.skipped_pages.length > 0) {
    // issue #83: OMR 救済不能で skip されたページ番号 (1-based)。
    result.skippedPages = body.skipped_pages;
  }
  return result;
}

/** from_json_audio の result を RegenerateAudioResult に変換する (PDF を含まない)。 */
function parseAudioResult(body: FingeringProcessResponse): RegenerateAudioResult {
  return {
    audioBlob: base64ToBlob(body.audio as string, 'audio/mpeg'),
    audioFileName: body.audio_filename as string,
    detectedBpm: body.detected_bpm,
  };
}

/** job_type ごとの result 型。result ペイロードの形は job_type で異なる (設計 §3.4)。 */
export interface JobResultByType {
  from_pdf: FingeringResult;
  from_json_both: FingeringResult;
  from_json_pdf: FingeringResult;
  from_json_audio: RegenerateAudioResult;
}

/**
 * job_type → result パース関数の対応表 (設計 §3.4 / §7.1)。
 * result ペイロードの形は job_type で異なるため、パースもここで 1 箇所に集約する。
 */
const RESULT_PARSERS: {
  [K in JobType]: (body: FingeringProcessResponse) => JobResultByType[K];
} = {
  from_pdf: parsePdfResult,
  from_json_both: parsePdfResult,
  from_json_pdf: parsePdfResult,
  from_json_audio: parseAudioResult,
};

/** fetch レスポンスがエラーなら error メッセージで throw する。
 *  issue #43: body.error_kind があれば ProcessingError として throw し、 errorKind を運ぶ。
 *  例: 413 + { error: '...', error_kind: 'file_too_large' } → POST 即時 reject 経路で文言分岐可。
 *  issue #92: 404 は notFound=true の ProcessingError として throw し、 呼び出し側 (getJobResult →
 *  restoreJobResultCore) が transient 失敗 (500/network) と区別できるようにする。
 *  なお getJobStatus は throwIfError 前に 404→expired を早期 return するため本分岐の影響を受けない。 */
async function throwIfError(res: Response): Promise<void> {
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    if (errBody.error_kind) {
      throw new ProcessingError(
        errBody.error || `HTTP ${res.status}`,
        errBody.error_kind as JobErrorKind,
      );
    }
    if (res.status === 404) {
      throw new ProcessingError(errBody.error || `HTTP ${res.status}`, undefined, true);
    }
    throw new Error(errBody.error || `HTTP ${res.status}`);
  }
}

/** GET /fingering/jobs/<id> を 1 回叩き状態を返す。404 は status='expired'。 */
async function fetchJobStatus(jobId: string): Promise<JobStatusResponse> {
  const res = await fetch(`${API_BASE_URL}/fingering/jobs/${jobId}`);
  if (res.status === 404) {
    return { status: 'expired' };
  }
  await throwIfError(res);
  const body = await res.json();
  return {
    status: body.status as JobStatusValue,
    error: body.error as string | undefined,
    // issue #54: score-engine の error_kind (snake_case) → JS の errorKind (camelCase) に変換。
    errorKind: body.error_kind as JobErrorKind | undefined,
    queuePosition: body.queue_position as number | undefined,
  };
}

/** GET /fingering/jobs/<id>/result を叩き生レスポンスを返す。 */
async function fetchJobResult(jobId: string): Promise<FingeringProcessResponse> {
  const res = await fetch(`${API_BASE_URL}/fingering/jobs/${jobId}/result`);
  await throwIfError(res);
  return (await res.json()) as FingeringProcessResponse;
}

/**
 * from_pdf 投入 (processFromPdf / submitFromPdf 共通) の FormData を組み立てる。
 * 未指定の任意フィールドは append しない (backend は欠落を default 扱い)。
 */
export function buildFromPdfForm(req: FingeringProcessFromPdfRequest): FormData {
  const form = new FormData();
  // issue #131: fileName 指定時は multipart filename を実名で明示する (name 無し Blob 対策)。
  if (req.fileName) {
    form.append('file', req.pdfBlob, req.fileName);
  } else {
    form.append('file', req.pdfBlob);
  }
  form.append('part', req.part);
  form.append('mode', req.mode);
  // issue #152: 命名ソース (元タイトル)。 backend はこれを起点に PDF/MP3/omfz 名を組む。
  if (req.originalTitle) form.append('original_title', req.originalTitle);
  if (req.scordatura) form.append('scordatura', req.scordatura);
  if (req.bpm !== undefined) form.append('bpm', String(req.bpm));
  if (req.output) form.append('output', req.output);
  if (req.colorMode) form.append('color_mode', req.colorMode);
  // issue #67: backend 自動保存用 Drive token を FormData に乗せる (未指定なら silent skip)
  if (req.driveAccessToken) form.append('drive_access_token', req.driveAccessToken);
  if (req.driveParentFolderId) form.append('drive_parent_folder_id', req.driveParentFolderId);
  // issue #98: ジョブ完了通知メールの送信先 (未指定なら通知しない、backend BTask3 とキー一致)
  if (req.notifyEmail) form.append('notify_email', req.notifyEmail);
  // issue #111: 自動保存フラグ (backend は auto_save == 'true' で on_done の Drive 保存/通知をガード)
  if (req.autoSave !== undefined) form.append('auto_save', String(req.autoSave));
  return form;
}

export const fingeringApi = {
  /**
   * ジョブを投入し done まで polling して結果を取得する汎用 async 関数。
   * POST /fingering/jobs → GET /fingering/jobs/<id> 反復 → GET /fingering/jobs/<id>/result。
   * polling は hook 非依存の setTimeout ループ。進捗は onProgress で通知する。
   * 戻り値は job_type ごとに異なる (RESULT_PARSERS に対応)。
   */
  async processFingeringAsync<T extends JobType>(
    jobType: T,
    payload: FormData,
    onProgress?: (progress: JobProgress) => void,
  ): Promise<JobResultByType[T]> {
    payload.set('job_type', jobType);
    // issue #80 案 B: env セット時のみ score-engine 直接、 未設定は既存 SWA 経由
    // issue #115 T3: cold start を待てるよう延長 timeout 付き POST。
    const submitRes = await postFormWithTimeout(getSubmitUrl(), payload);
    await throwIfError(submitRes);
    const submitBody = await submitRes.json();
    const jobId = submitBody.job_id as string;
    onProgress?.({
      jobId,
      status: 'queued',
      queuePosition: submitBody.queue_position as number | undefined,
    });

    // 初回ポーリングは即時実行、以降は POLL_INTERVAL_MS 間隔。
    let first = true;
    // 連続 expired 回数。 expired 以外を見たらリセットする (issue #36 F-2)。
    let expiredCount = 0;
    for (;;) {
      if (!first) {
        await sleep(POLL_INTERVAL_MS);
      }
      first = false;

      const status = await fetchJobStatus(jobId);
      // done は onProgress で通知しない。done を localStorage に書き込むと
      // race window 内 reload で前回結果カードが復活する (issue #9 バグ B、案 B-A')。
      // 将来 SUCCESS_STATUSES を拡張するときは、この抑制範囲も再検討する。
      if (!SUCCESS_STATUSES.has(status.status)) {
        onProgress?.({ jobId, status: status.status, queuePosition: status.queuePosition });
      }

      if (SUCCESS_STATUSES.has(status.status)) {
        const body = await fetchJobResult(jobId);
        return RESULT_PARSERS[jobType](body);
      }
      if (status.status === 'expired') {
        // 404→expired は transient の可能性がある。 budget 未満なら即 throw せず再 poll し、
        // processing 等が来れば復帰を狙う (issue #36 F-2)。
        expiredCount += 1;
        if (expiredCount < EXPIRED_RETRY_BUDGET) {
          continue;
        }
        // issue #54: ProcessingError に統一 (errorKind は expired 経路では未定義)。
        throw new ProcessingError(status.error ?? 'ジョブの有効期限が切れました', status.errorKind);
      }
      // expired 以外を見たら連続カウンタをリセット。
      expiredCount = 0;
      if (FAILURE_STATUSES.has(status.status)) {
        // issue #54: ProcessingError に統一、 score-engine 由来の errorKind (omr_timeout 等) を運ぶ。
        throw new ProcessingError(status.error ?? 'ジョブの処理に失敗しました', status.errorKind);
      }
    }
  },

  /**
   * PDF から運指生成 (job_type=from_pdf)。OMR を含む、最も時間のかかる処理。
   * onProgress を渡すと queued/processing の進捗と jobId が通知される。
   */
  async processFromPdf(
    req: FingeringProcessFromPdfRequest,
    onProgress?: (progress: JobProgress) => void,
  ): Promise<FingeringResult> {
    return this.processFingeringAsync('from_pdf', buildFromPdfForm(req), onProgress);
  },

  /**
   * issue #76 Phase 3 sub-I: fire-and-forget POST。 polling は内蔵せず POST 即 resolve。
   * 完了監視は呼び出し側 (jobTracker singleton) が引き受ける。
   */
  async submitFromPdf(
    req: FingeringProcessFromPdfRequest,
  ): Promise<{ jobId: string; queuePosition?: number }> {
    // fire-and-forget 経路も同期版 processFromPdf と同一フィールドを送る (buildFromPdfForm で対称化)。
    const form = buildFromPdfForm(req);
    form.set('job_type', 'from_pdf');
    // issue #80 案 B: env セット時のみ score-engine 直接、 未設定は既存 SWA 経由
    // issue #115 T3: cold start を待てるよう延長 timeout 付き POST。
    const submitRes = await postFormWithTimeout(getSubmitUrl(), form);
    await throwIfError(submitRes);
    const body = await submitRes.json();
    return {
      jobId: body.job_id as string,
      queuePosition: body.queue_position as number | undefined,
    };
  },

  /**
   * 編集済み JSON から PDF + audio を再生成 (job_type=from_json_both)。
   * onProgress を渡すと queued/processing の進捗と jobId が通知される。
   */
  async regenerateBoth(
    originalPdf: Blob,
    mxl: Blob,
    fingeringJson: string,
    bpm?: number,
    onProgress?: (progress: JobProgress) => void,
    colorMode?: PdfColorMode,
    mode?: FingeringMode,
    // issue #148: 完了通知 / 自動保存フラグ / 結果ファイル名 (from_pdf の同フィールドと対称)。
    opts?: RegenerateNotifyOptions,
  ): Promise<RegenerateBothResult> {
    const form = new FormData();
    form.append('original_pdf', originalPdf);
    form.append('mxl', mxl);
    form.append('fingering', fingeringJson);
    if (bpm !== undefined) form.append('bpm', String(bpm));
    if (colorMode) form.append('color_mode', colorMode);
    // issue #91: 再生成でも mode を送り backend が mode 別ファイル名規則を適用できるようにする
    // (from_pdf 投入時と同一フィールド名 'mode'・値 'score'|'part')。 未指定時 backend は part 番号付き fallback。
    if (mode) form.append('mode', mode);
    // issue #148: notify_email / auto_save / file_name を積む (未指定は積まない)。
    appendNotifyFields(form, opts);
    const result = await this.processFingeringAsync('from_json_both', form, onProgress);
    return {
      pdfBlob: result.pdfBlob,
      audioBlob: result.audioBlob as Blob,
      audioFileName: result.audioFileName as string,
    };
  },

  /**
   * 編集済み JSON から PDF のみ再生成 (job_type=from_json_pdf)。
   * onProgress を渡すと queued/processing の進捗と jobId が通知される。
   */
  async regeneratePdf(
    originalPdf: Blob,
    fingeringJson: string,
    onProgress?: (progress: JobProgress) => void,
    colorMode?: PdfColorMode,
    // issue #148: 完了通知 / 自動保存フラグ / 結果ファイル名 (from_pdf の同フィールドと対称)。
    opts?: RegenerateNotifyOptions,
  ): Promise<Blob> {
    const form = new FormData();
    form.append('original_pdf', originalPdf);
    form.append('fingering', fingeringJson);
    if (colorMode) form.append('color_mode', colorMode);
    // issue #148: notify_email / auto_save / file_name を積む (未指定は積まない)。
    appendNotifyFields(form, opts);
    const result = await this.processFingeringAsync('from_json_pdf', form, onProgress);
    return result.pdfBlob;
  },

  /**
   * 編集済み JSON から audio のみ再生成 (job_type=from_json_audio)。
   * onProgress を渡すと queued/processing の進捗と jobId が通知される。
   */
  async regenerateAudio(
    mxl: Blob,
    fingeringJson: string,
    bpm?: number,
    onProgress?: (progress: JobProgress) => void,
    mode?: FingeringMode,
    // issue #148: 完了通知 / 自動保存フラグ / 結果ファイル名 (from_pdf の同フィールドと対称)。
    opts?: RegenerateNotifyOptions,
  ): Promise<RegenerateAudioResult> {
    const form = new FormData();
    form.append('mxl', mxl);
    form.append('fingering', fingeringJson);
    if (bpm !== undefined) form.append('bpm', String(bpm));
    // issue #91: MP3 名も mode 別命名 (part 譜は part なし / 総譜は 1st-4th) のため mode を送る。
    if (mode) form.append('mode', mode);
    // issue #148: notify_email / auto_save / file_name を積む (未指定は積まない)。
    appendNotifyFields(form, opts);
    return this.processFingeringAsync('from_json_audio', form, onProgress);
  },

  /**
   * ジョブの進行状態を取得する (GET /fingering/jobs/<id>)。
   * 404 のときは throw せず status='expired' を返す (TTL 切れ / 不存在)。
   * useJobStatusPolling hook など UI 側の状態購読から利用する低レベル API。
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    return fetchJobStatus(jobId);
  },

  /**
   * 完了したジョブの結果を取得する (GET /fingering/jobs/<id>/result)。
   * レスポンスは JSON + base64。Blob 化して返す低レベル API。
   * 戻り値は PDF を含む job_type (from_pdf/from_json_both/from_json_pdf) を想定。
   */
  async getJobResult(jobId: string): Promise<FingeringResult> {
    const body = await fetchJobResult(jobId);
    return parsePdfResult(body);
  },
};
